/**
 * EtatDesLieuxModal — création d'un état des lieux (mig 215 + templates mig 216).
 *
 * Deux voies :
 *   · DEPUIS UN TEMPLATE (recommandé) : on choisit un template (= un lieu), et
 *     les points à vérifier sortent pré-remplis. Plus de ressaisie.
 *   · À BLANC : on saisit les points à la main (fallback).
 *
 * Après création, on redirige vers la fiche (qui hérite du planning, des photos
 * avant/après et du devis vocal).
 */
import { useEffect, useState } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { Button, Alert } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import { ChecklistEditor } from '@/pages/service-technique/ChecklistEditor';
import type { ChecklistItem } from '@roronoa-collectivites/shared-types';
import type { EdlTemplate } from '@/lib/api';

let seq = 0;
const newId = () => `pt-${Date.now()}-${seq++}`;

export function EtatDesLieuxModal({ onClose, onCreated, onExistant }: {
  onClose: () => void;
  onCreated: (id: string) => void;
  /** Appelé si le lieu a déjà un EDL actif : on redirige vers sa mise à jour. */
  onExistant?: (edlId: string, numero?: string) => void;
}) {
  const [templates, setTemplates] = useState<EdlTemplate[]>([]);
  const [templateId, setTemplateId] = useState<string>('');
  const [lieuId, setLieuId] = useState<string | null>(null);
  const [sujet, setSujet] = useState('');
  const [lieu, setLieu] = useState('');
  const [description, setDescription] = useState('');
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.serviceTechnique.edlTemplates.list().then(setTemplates).catch(() => setTemplates([]));
  }, []);

  // Choix d'un template → pré-remplissage (sujet, lieu, points en état 'ok').
  function choisirTemplate(id: string) {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (!t) { setLieuId(null); return; }
    setLieuId(t.lieuId);
    if (t.lieuNom) setLieu(t.lieuNom);
    if (!sujet.trim()) setSujet(t.nom);
    setChecklist(t.points.map((p) => ({ id: newId(), libelle: p.libelle, etat: 'ok' as const, note: '', coutEstime: null, piece: p.piece ?? null })));
  }

  async function creer() {
    if (sujet.trim().length < 3) { setError('Donnez un intitulé (ex : « Sortie salle des fêtes »)'); return; }
    if (checklist.some((c) => c.libelle.trim() === '')) { setError('Chaque point doit avoir un libellé'); return; }
    setSaving(true); setError(null);
    try {
      const dto = await api.serviceTechnique.signalements.creerEtatDesLieux({
        sujet: sujet.trim(),
        description: description.trim() || undefined,
        localisationAdresse: lieu.trim() || undefined,
        checklist,
        lieuId: lieuId ?? undefined,
        templateId: templateId || undefined,
      });
      onCreated(dto.id);
    } catch (e) {
      // Garde stricte : un EDL existe déjà pour ce lieu → rediriger vers sa mise à jour.
      if (e instanceof ApiException && e.payload.code === 'EDL_LIEU_DEJA_EXISTANT') {
        const details = e.payload.details as { edlId?: string; numero?: string } | undefined;
        if (details?.edlId && onExistant) {
          onExistant(details.edlId, details.numero);
          return;
        }
      }
      setError(e instanceof ApiException ? e.message : 'Erreur');
    }
    finally { setSaving(false); }
  }

  return (
    <Dialog open onClose={onClose} title="Nouvel état des lieux" size="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Annuler</Button>
        <Button variant="primary" loading={saving} onClick={() => void creer()}>Créer l'état des lieux</Button>
      </>}>
      <div className="space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        {templates.length > 0 && (
          <div className="rounded-r-lg border border-tech/40 bg-tech-tint/20 p-3">
            <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-tech-fg">Partir d'un template</label>
            <select value={templateId} onChange={(e) => choisirTemplate(e.target.value)}
              className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
              <option value="">— À blanc (saisie manuelle) —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.nom}{t.lieuNom ? ` · ${t.lieuNom}` : ''}</option>)}
            </select>
            <p className="mt-1.5 text-xs text-ink-subtle">Choisir un template pré-remplit les points à vérifier pour ce lieu.</p>
          </div>
        )}

        <div>
          <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Intitulé *</label>
          <input value={sujet} onChange={(e) => setSujet(e.target.value)} placeholder="Ex : Sortie salle des fêtes — M. Dupont" className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
        </div>
        <div>
          <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Lieu / bien concerné</label>
          <input value={lieu} onChange={(e) => setLieu(e.target.value)} placeholder="Salle, logement, matériel…" className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
        </div>
        <div>
          <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Contexte (optionnel)</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Remarques générales…" className="w-full rounded-r-md border border-border bg-surface/60 px-2 py-2 text-sm text-ink" />
        </div>
        <div>
          <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Points à inspecter</label>
          <ChecklistEditor items={checklist} onChange={setChecklist} />
        </div>
        <p className="text-xs text-ink-subtle">Après création, vous pourrez planifier l'intervention, ajouter des photos avant/après, et — pour un point dégradé — demander un devis à un prestataire.</p>
      </div>
    </Dialog>
  );
}
